import { alpha } from '@mui/material/styles';

const BODY_BG_DARK = '#141820';

const darkCardBg = `linear-gradient(165deg, ${alpha('#ffffff', 0.04)} 0%, ${BODY_BG_DARK} 42%)`;

/** Accent colors for room board statuses (vacant = neutral, occupied = green). */
export const ROOM_BOARD_ACCENT = {
  vacant: '#94a3b8',
  occupied: '#4caf50',
  pending_checkout: '#c79100',
  hourly: '#00897b',
  booked: '#ff9800',
  blocked: '#ef5350',
  checked_out: '#2196f3',
  cancelled: '#ef5350',
};

/** Summary chip colors on dashboard workspace headers. */
export const WORKSPACE_STAT_COLORS = {
  total: '#94a3b8',
  vacant: '#94a3b8',
  occupied: '#4caf50',
  pending_checkout: '#c79100',
  hourly: '#00897b',
  booked: '#ff9800',
  blocked: '#ef5350',
  checked_out: '#2196f3',
  cancelled: '#ef5350',
};

export function getRoomBoardStatsChipTokens(isDark, kind) {
  const neutralBd = isDark ? alpha('#fff', 0.2) : alpha('#000', 0.14);
  const neutralFg = isDark ? alpha('#fff', 0.78) : alpha('#000', 0.68);
  const neutralHover = isDark ? alpha('#fff', 0.08) : alpha('#000', 0.04);

  const tokens = {
    total: { bd: neutralBd, fg: neutralFg, hover: neutralHover },
    vacant: { bd: neutralBd, fg: neutralFg, hover: neutralHover },
    occupied: {
      bd: isDark ? alpha(ROOM_BOARD_ACCENT.occupied, 0.55) : alpha('#2e7d32', 0.45),
      fg: isDark ? '#a5d6a7' : '#2e7d32',
      hover: alpha(ROOM_BOARD_ACCENT.occupied, isDark ? 0.14 : 0.1),
    },
    pending_checkout: {
      bd: isDark ? alpha(ROOM_BOARD_ACCENT.pending_checkout, 0.65) : alpha('#a67c00', 0.55),
      fg: isDark ? '#e6c200' : '#8a6d00',
      hover: alpha(ROOM_BOARD_ACCENT.pending_checkout, isDark ? 0.16 : 0.12),
    },
    hourly: {
      bd: isDark ? alpha(ROOM_BOARD_ACCENT.hourly, 0.6) : alpha('#00695c', 0.5),
      fg: isDark ? '#80cbc4' : '#00695c',
      hover: alpha(ROOM_BOARD_ACCENT.hourly, isDark ? 0.16 : 0.12),
    },
    booked: {
      bd: isDark ? alpha(ROOM_BOARD_ACCENT.booked, 0.5) : alpha('#f57c00', 0.45),
      fg: isDark ? '#ffcc80' : '#e65100',
      hover: alpha(ROOM_BOARD_ACCENT.booked, isDark ? 0.12 : 0.1),
    },
    blocked: {
      bd: isDark ? alpha(ROOM_BOARD_ACCENT.blocked, 0.55) : alpha('#d32f2f', 0.45),
      fg: isDark ? '#ef9a9a' : '#c62828',
      hover: alpha(ROOM_BOARD_ACCENT.blocked, isDark ? 0.12 : 0.08),
    },
    checked_out: {
      bd: isDark ? alpha(ROOM_BOARD_ACCENT.checked_out, 0.5) : alpha('#1976d2', 0.4),
      fg: isDark ? '#90caf9' : '#1565c0',
      hover: alpha(ROOM_BOARD_ACCENT.checked_out, isDark ? 0.12 : 0.1),
    },
    cancelled: {
      bd: isDark ? alpha(ROOM_BOARD_ACCENT.cancelled, 0.55) : alpha('#d32f2f', 0.45),
      fg: isDark ? '#ef9a9a' : '#c62828',
      hover: alpha(ROOM_BOARD_ACCENT.cancelled, isDark ? 0.12 : 0.08),
    },
  };

  return tokens[kind] || tokens.total;
}

export function getUltraRoomStatusPalette(status, isDark) {
  const neutralVacant = {
    bg: isDark ? darkCardBg : `linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)`,
    bodyBg: 'transparent',
    heroGlow: isDark ? 0.06 : 0.04,
    border: isDark ? alpha(ROOM_BOARD_ACCENT.vacant, 0.22) : alpha('#64748b', 0.16),
    text: isDark ? alpha('#fff', 0.72) : '#64748b',
    dot: isDark ? ROOM_BOARD_ACCENT.vacant : '#64748b',
    shadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.35)' : '0 2px 8px rgba(15, 23, 42, 0.06)',
  };

  const palettes = {
    vacant: neutralVacant,
    occupied: {
      bg: isDark
        ? darkCardBg
        : 'linear-gradient(165deg, #e8f5e9 0%, #f1f8f4 100%)',
      bodyBg: isDark ? 'transparent' : alpha(ROOM_BOARD_ACCENT.occupied, 0.07),
      heroGlow: isDark ? 0.1 : 0.12,
      border: isDark ? alpha(ROOM_BOARD_ACCENT.occupied, 0.4) : alpha('#2e7d32', 0.36),
      text: isDark ? '#a5d6a7' : '#1b5e20',
      dot: ROOM_BOARD_ACCENT.occupied,
      shadow: isDark
        ? '0 2px 8px rgba(0, 0, 0, 0.35)'
        : '0 2px 8px rgba(76, 175, 80, 0.14)',
    },
    pending_checkout: {
      bg: isDark
        ? darkCardBg
        : 'linear-gradient(165deg, #fff8e1 0%, #ffecb3 100%)',
      bodyBg: isDark ? 'transparent' : alpha(ROOM_BOARD_ACCENT.pending_checkout, 0.12),
      heroGlow: isDark ? 0.12 : 0.14,
      border: isDark ? alpha(ROOM_BOARD_ACCENT.pending_checkout, 0.55) : alpha('#a67c00', 0.5),
      text: isDark ? '#e6c200' : '#8a6d00',
      dot: ROOM_BOARD_ACCENT.pending_checkout,
      shadow: isDark
        ? '0 2px 8px rgba(0, 0, 0, 0.35)'
        : '0 2px 8px rgba(199, 145, 0, 0.2)',
    },
    hourly: {
      bg: isDark
        ? darkCardBg
        : 'linear-gradient(165deg, #e0f2f1 0%, #b2dfdb 100%)',
      bodyBg: isDark ? 'transparent' : alpha(ROOM_BOARD_ACCENT.hourly, 0.1),
      heroGlow: isDark ? 0.12 : 0.14,
      border: isDark ? alpha(ROOM_BOARD_ACCENT.hourly, 0.5) : alpha('#00695c', 0.45),
      text: isDark ? '#80cbc4' : '#00695c',
      dot: ROOM_BOARD_ACCENT.hourly,
      shadow: isDark
        ? '0 2px 8px rgba(0, 0, 0, 0.35)'
        : '0 2px 8px rgba(0, 137, 123, 0.18)',
    },
    booked: {
      bg: isDark
        ? darkCardBg
        : 'linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)',
      bodyBg: isDark ? 'transparent' : alpha(ROOM_BOARD_ACCENT.booked, 0.05),
      heroGlow: isDark ? 0.09 : 0.1,
      border: isDark ? alpha(ROOM_BOARD_ACCENT.booked, 0.35) : alpha('#f57c00', 0.35),
      text: isDark ? '#ffcc80' : '#c2410c',
      dot: ROOM_BOARD_ACCENT.booked,
      shadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.35)' : '0 2px 8px rgba(255, 152, 0, 0.12)',
    },
    blocked: {
      bg: isDark
        ? darkCardBg
        : 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
      bodyBg: isDark ? 'transparent' : alpha(ROOM_BOARD_ACCENT.blocked, 0.05),
      heroGlow: isDark ? 0.09 : 0.1,
      border: isDark ? alpha(ROOM_BOARD_ACCENT.blocked, 0.35) : alpha('#dc2626', 0.32),
      text: isDark ? '#fca5a5' : '#b91c1c',
      dot: ROOM_BOARD_ACCENT.blocked,
      shadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.35)' : '0 2px 8px rgba(244, 67, 54, 0.12)',
    },
    checked_out: {
      bg: isDark
        ? darkCardBg
        : 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
      bodyBg: isDark ? 'transparent' : alpha(ROOM_BOARD_ACCENT.checked_out, 0.04),
      heroGlow: isDark ? 0.08 : 0.08,
      border: isDark ? alpha(ROOM_BOARD_ACCENT.checked_out, 0.32) : alpha('#2563eb', 0.28),
      text: isDark ? '#90caf9' : '#1d4ed8',
      dot: ROOM_BOARD_ACCENT.checked_out,
      shadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.35)' : '0 1px 4px rgba(33, 150, 243, 0.1)',
    },
    cancelled: {
      bg: isDark
        ? darkCardBg
        : 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
      bodyBg: isDark ? 'transparent' : alpha(ROOM_BOARD_ACCENT.cancelled, 0.05),
      heroGlow: isDark ? 0.08 : 0.08,
      border: isDark ? alpha(ROOM_BOARD_ACCENT.cancelled, 0.32) : alpha('#dc2626', 0.28),
      text: isDark ? '#fca5a5' : '#b91c1c',
      dot: ROOM_BOARD_ACCENT.cancelled,
      shadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.35)' : '0 1px 4px rgba(239, 83, 80, 0.1)',
    },
  };

  return palettes[status] || neutralVacant;
}
